import * as Yup from 'yup';

/** TYPE DEFINITIONS */
type ExternalOptions = Record<string, { options: string[] }>;

/** FUNCTIONS TO VALIDATIONS */

// Convert IP to Long (integer)
const ipToLong = (ip: string) => {
  if (!ip) return 0;
  return (
    ip
      .split('.')
      .reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0
  );
};

// Verify if two IPs are in the same subnet based on the mask
const isSameSubnet = (ip1: string, ip2: string, mask: string) => {
  if (!ip1 || !ip2 || !mask) return true;
  const ip1Long = ipToLong(ip1);
  const ip2Long = ipToLong(ip2);
  const maskLong = ipToLong(mask);
  // Bitwise operation AND to compare the networks
  return (ip1Long & maskLong) === (ip2Long & maskLong);
};

// Verify if IP A is greater than IP B
const isIpGreater = (ipHigh: string, ipLow: string) => {
  if (!ipHigh || !ipLow) return true;
  return ipToLong(ipHigh) > ipToLong(ipLow);
};

// --- Regexs ---
const ipPattern =
  '(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)';
const singleIpRegex = new RegExp(`^${ipPattern}$`);

// Returns ('calculates') the network address (first IP)
const getNetworkAddress = (ip: string, mask: string) => {
  return (ipToLong(ip) & ipToLong(mask)) >>> 0;
};

// Verifies if the IP is the network address
const isReservedIPNetwork = (ip: string, mask: string) => {
  if (!ip || !mask) return false;
  const ipLong = ipToLong(ip);
  const network = getNetworkAddress(ip, mask);
  return ipLong === network;
};

// Returns ('calculates') the broadcast address (last IP)
const getBroadcastAddress = (ip: string, mask: string) => {
  const network = getNetworkAddress(ip, mask);
  const invertedMask = ~ipToLong(mask) >>> 0;
  return (network | invertedMask) >>> 0;
};

// Verifies if the IP is reserved (network or broadcast)
const isReservedIPBroadcast = (ip: string, mask: string) => {
  if (!ip || !mask) return false;
  const ipLong = ipToLong(ip);
  const broadcast = getBroadcastAddress(ip, mask);
  return ipLong === broadcast;
};

// Cross and general validations about LAN Network Settings
export const getLanSchema = (t: (key: string) => string) => {
  const schema = Yup.object().shape({
    ipAddress: Yup.string()
      .matches(singleIpRegex, t('lanSettings.ipAddress.invalid'))
      .required(t('lanSettings.ipAddress.required'))
      .test(
        'not-reserved-network',
        t('lanSettings.ipAddress.networkReserved'),
        function (value) {
          const { mask } = this.parent;
          return !isReservedIPNetwork(value, mask);
        }
      )
      .test(
        'not-reserved-broadcast',
        t('lanSettings.ipAddress.broadcastReserved'),
        function (value) {
          const { mask } = this.parent;
          return !isReservedIPBroadcast(value, mask);
        }
      ),

    mask: Yup.string()
      .matches(singleIpRegex, t('lanSettings.mask.invalid'))
      .required(t('lanSettings.mask.required')),

    gateway: Yup.string()
      .matches(singleIpRegex, t('lanSettings.gateway.invalid'))
      .required(t('lanSettings.gateway.required'))
      .test(
        'gateway-subnet',
        t('lanSettings.gateway.subnetMismatch'),
        function (value) {
          const { ipAddress, mask } = this.parent;
          return isSameSubnet(ipAddress, value, mask);
        }
      )
      .test(
        'not-reserved-network',
        t('lanSettings.gateway.networkReserved'),
        function (value) {
          const { mask } = this.parent;
          return !isReservedIPNetwork(value, mask);
        }
      )
      .test(
        'not-reserved-broadcast',
        t('lanSettings.gateway.broadcastReserved'),
        function (value) {
          const { mask } = this.parent;
          return !isReservedIPBroadcast(value, mask);
        }
      ),

    dnsServers: Yup.string()
      .required(t('lanSettings.dnsServers.required'))
      .test(
        'valid-dns-list',
        t('lanSettings.dnsServers.invalidList'),
        (value) => {
          if (!value) return false;
          return value.split(',').every((ip) => singleIpRegex.test(ip.trim()));
        }
      ),

    dhcpEnable: Yup.boolean(),

    dhcpMin: Yup.string().when('dhcpEnable', {
      is: true,
      then: (schema) =>
        schema
          .matches(singleIpRegex, t('lanSettings.dhcp.invalidIp'))
          .required(t('lanSettings.dhcp.required'))
          .test(
            'dhcp-min-subnet',
            t('lanSettings.dhcp.min.subnetMismatch'),
            function (value) {
              const { ipAddress, mask } = this.parent;
              return isSameSubnet(ipAddress, value, mask);
            }
          )
          .test(
            'dhcp-min-no-lan',
            t('lanSettings.dhcp.min.sameAsLan'),
            function (value) {
              return value !== this.parent.ipAddress;
            }
          )
          .test(
            'dhcp-min-no-gateway',
            t('lanSettings.dhcp.min.sameAsGateway'),
            function (value) {
              return value !== this.parent.gateway;
            }
          )
          .test(
            'not-reserved-network',
            t('lanSettings.dhcp.min.networkReserved'),
            function (value) {
              const { mask } = this.parent;
              return !isReservedIPNetwork(value, mask);
            }
          )
          .test(
            'not-reserved-broadcast',
            t('lanSettings.dhcp.min.broadcastReserved'),
            function (value) {
              const { mask } = this.parent;
              return !isReservedIPBroadcast(value, mask);
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    dhcpMax: Yup.string().when('dhcpEnable', {
      is: true,
      then: (schema) =>
        schema
          .matches(singleIpRegex, t('lanSettings.dhcp.invalidIp'))
          .required(t('lanSettings.dhcp.required'))
          .test(
            'dhcp-max-subnet',
            t('lanSettings.dhcp.max.subnetMismatch'),
            function (value) {
              const { ipAddress, mask } = this.parent;
              return isSameSubnet(ipAddress, value, mask);
            }
          )
          .test(
            'dhcp-max-no-lan',
            t('lanSettings.dhcp.max.sameAsLan'),
            function (value) {
              return value !== this.parent.ipAddress;
            }
          )
          .test(
            'dhcp-max-no-gateway',
            t('lanSettings.dhcp.max.sameAsGateway'),
            function (value) {
              return value !== this.parent.gateway;
            }
          )
          .test(
            'dhcp-range',
            t('lanSettings.dhcp.max.rangeError'),
            function (value) {
              const { dhcpMin } = this.parent;
              return isIpGreater(value, dhcpMin);
            }
          )
          .test(
            'not-reserved-network',
            t('lanSettings.dhcp.max.networkReserved'),
            function (value) {
              const { mask } = this.parent;
              return !isReservedIPNetwork(value, mask);
            }
          )
          .test(
            'not-reserved-broadcast',
            t('lanSettings.dhcp.max.broadcastReserved'),
            function (value) {
              const { mask } = this.parent;
              return !isReservedIPBroadcast(value, mask);
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),

    leasing: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(1)
      .max(604800)
      .nullable(),
  });
  return schema as unknown as Yup.ObjectSchema<Record<string, unknown>>;
};

/** FUNCTIONS TO VALIDATIONS */

// Complex IPv6 Regex
const ipv6Pattern =
  '^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$';
const singleIpv6Regex = new RegExp(ipv6Pattern);

// --- Helpers ---

// Check if mode allows static IP editing (IPv4 — all modes need basic IP config)
const isIpv4EditableMode = (mode: string) => {
  const modes = ['PPPOE', 'DHCP', 'STATIC', 'BRIDGE'];
  return modes.includes(mode);
};

// Check if mode requires manual IPv6 configuration (only STATIC)
// PPPOE and DHCP obtain IPv6 automatically, so manual input is not needed
const isStaticIpv6Mode = (mode: string) => {
  return mode === 'STATIC';
};

// Cross and general validations about WAN Settings
export const getWanSchema = (
  t: (key: string) => string,
  externalOptions?: ExternalOptions
) => {
  // Use backend options or defaults
  const connectionTypeOptions = externalOptions?.connectionType?.options || [
    'DHCP',
    'PPPOE',
    'STATIC',
    'BRIDGE',
  ];
  const serviceTypeOptions = externalOptions?.serviceType?.options || [
    'INTERNET',
    'VOIP',
    'IPTV',
  ];
  const connectionModeOptions = externalOptions?.connectionMode?.options || [
    'auto',
    'demand',
    'persistent',
  ];
  const ipv6AddressingTypeOptions = externalOptions?.ipv6AddressingType
    ?.options || ['DHCPv6', 'SLAAC', 'STATIC'];
  const duplexModeOptions = externalOptions?.duplexMode?.options || [
    'auto',
    'half',
    'full',
  ];
  const portSpeedOptions = externalOptions?.portSpeed?.options || [
    'auto',
    '10',
    '100',
    '1000',
  ];

  const schema = Yup.object().shape({
    // --- General Config ---
    connectionType: Yup.string()
      .required(t('wanSettings.connectionType.required'))
      .oneOf(connectionTypeOptions, t('wanSettings.connectionType.invalid')),

    serviceType: Yup.string()
      .required(t('wanSettings.serviceType.required'))
      .test(
        'valid-service-type',
        t('wanSettings.serviceType.invalid'),
        function (value) {
          if (!value || !value.trim()) {
            return this.createError({
              message: t('wanSettings.serviceType.required'),
            });
          }

          const items = value
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v);

          if (items.length === 0) {
            return this.createError({
              message: t('wanSettings.serviceType.required'),
            });
          }

          return items.every((item) =>
            serviceTypeOptions.includes(String(item))
          );
        }
      ),

    enable: Yup.boolean(),

    mtu: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required(t('wanSettings.mtu.required'))
      .positive()
      .integer()
      .test('mtu-limit', t('wanSettings.mtu.limit'), function (value) {
        const { connectionType } = this.parent;
        if (connectionType === 'PPPoE') {
          return (value || 0) <= 1492;
        }
        return (value || 0) <= 1500;
      }),

    // --- IPv4 Global Rules ---
    ipv4Enable: Yup.boolean(),

    ipv4: Yup.string().when(['connectionType', 'ipv4Enable'], {
      is: (type: string, enable: boolean) => isIpv4EditableMode(type) && enable,
      then: (schema) =>
        schema
          .matches(singleIpRegex, t('wanSettings.ipv4.invalid'))
          .required(t('wanSettings.ipv4.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    mask: Yup.string().when(['connectionType', 'ipv4Enable'], {
      is: (type: string, enable: boolean) => isIpv4EditableMode(type) && enable,
      then: (schema) =>
        schema
          .matches(singleIpRegex, t('wanSettings.mask.invalid'))
          .required(t('wanSettings.mask.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    ipv4Gateway: Yup.string().when(['connectionType', 'ipv4Enable'], {
      is: (type: string, enable: boolean) => isIpv4EditableMode(type) && enable,
      then: (schema) =>
        schema
          .matches(singleIpRegex, t('wanSettings.gateway.invalid'))
          .required(t('wanSettings.gateway.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    // --- IPv6 Global Rules ---
    ipv6Enable: Yup.boolean(),

    ipv6: Yup.string().when(['connectionType', 'ipv6Enable'], {
      is: (type: string, enable: boolean) => isStaticIpv6Mode(type) && enable,
      then: (schema) =>
        schema
          .matches(singleIpv6Regex, t('wanSettings.ipv6.invalid'))
          .required(t('wanSettings.ipv6.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    ipv6PrefixLength: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when(['connectionType', 'ipv6Enable'], {
        is: (type: string, enable: boolean) => isStaticIpv6Mode(type) && enable,
        then: (schema) =>
          schema
            .required(t('wanSettings.ipv6Prefix.required'))
            .min(32, t('wanSettings.ipv6Prefix.range'))
            .max(128, t('wanSettings.ipv6Prefix.range')),
        otherwise: (schema) => schema.notRequired(),
      }),

    ipv6Gateway: Yup.string().when(['connectionType', 'ipv6Enable'], {
      is: (type: string, enable: boolean) => isStaticIpv6Mode(type) && enable,
      then: (schema) =>
        schema
          .matches(singleIpv6Regex, t('wanSettings.ipv6Gateway.invalid'))
          .required(t('wanSettings.ipv6Gateway.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    ipv6AddressingType: Yup.string()
      .nullable()
      .when(['connectionType', 'ipv6Enable'], {
        is: (type: string, enable: boolean) =>
          ['PPPOE', 'DHCP'].includes(type) && enable,
        then: (schema) =>
          schema
            .required(t('wanSettings.ipv6AddressingType.required'))
            .oneOf(
              ipv6AddressingTypeOptions,
              t('wanSettings.ipv6AddressingType.invalid')
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    // --- VLAN Rules ---
    vlanEnable: Yup.boolean(),

    vlan: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('vlanEnable', {
        is: true,
        then: (schema) =>
          schema
            .required(t('wanSettings.vlan.required'))
            .min(1, t('wanSettings.vlan.range'))
            .max(4094, t('wanSettings.vlan.range')),
        otherwise: (schema) => schema.notRequired(),
      }),

    // --- NAT Rules ---
    nat: Yup.boolean(),
    fullConeNat: Yup.boolean(),
    natBoost: Yup.boolean(),

    // --- PPPoE Rules ---
    userPPPoE: Yup.string().when('connectionType', {
      is: 'PPPoE',
      then: (schema) => schema.required(t('wanSettings.pppoe.user.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    passwordPPPoE: Yup.string().when('connectionType', {
      is: 'PPPoE',
      then: (schema) =>
        schema.required(t('wanSettings.pppoe.password.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    connectionMode: Yup.string().when('connectionType', {
      is: 'PPPoE',
      then: (schema) =>
        schema
          .required(t('wanSettings.pppoe.mode.required'))
          .oneOf(connectionModeOptions, t('wanSettings.pppoe.mode.invalid')),
      otherwise: (schema) => schema.notRequired(),
    }),

    // --- Read Only / Optional fields (to allow pass-through) ---
    alias: Yup.string().nullable(),
    macAddress: Yup.string().nullable(),
    portSpeed: Yup.string()
      .nullable()
      .test('valid-port-speed', t('wanSettings.portSpeed.invalid'), (value) => {
        if (!value) return true;
        return portSpeedOptions.includes(String(value));
      }),
    duplexMode: Yup.string()
      .nullable()
      .test('valid-duplex', t('wanSettings.duplexMode.invalid'), (value) => {
        if (!value) return true;
        return duplexModeOptions.includes(String(value));
      }),
    ipv4Dns: Yup.string().nullable(),
    ipv6Dns: Yup.string().nullable(),

    // Stats
    bytesSent: Yup.number().nullable(),
    bytesReceived: Yup.number().nullable(),
    packetsSent: Yup.number().nullable(),
    packetsReceived: Yup.number().nullable(),
    errorsSent: Yup.number().nullable(),
    errorsReceived: Yup.number().nullable(),
  });

  return schema as unknown as Yup.ObjectSchema<Record<string, unknown>>;
};

// Bridge configuration schema (similar to WAN but with BRIDGE-specific rules)
export const getBridgeSchema = (
  t: (key: string) => string,
  externalOptions?: ExternalOptions
) => {
  // Use backend options or defaults
  const connectionTypeOptions = externalOptions?.connectionType?.options || [
    'DHCP',
    'PPPOE',
    'STATIC',
    'BRIDGE',
  ];
  const serviceTypeOptions = externalOptions?.serviceType?.options || [
    'INTERNET',
    'VOIP',
    'IPTV',
  ];
  const ipv6AddressingTypeOptions = externalOptions?.c?.options || [
    'DHCPv6',
    'SLAAC',
    'AUTO',
  ];

  const schema = Yup.object().shape({
    // --- General Config ---
    connectionType: Yup.string()
      .required(t('wanSettings.connectionType.required'))
      .oneOf(connectionTypeOptions, t('wanSettings.connectionType.invalid')),

    serviceType: Yup.string()
      .required(t('wanSettings.serviceType.required'))
      .test(
        'valid-service-type',
        t('wanSettings.serviceType.invalid'),
        function (value) {
          if (!value || !value.trim()) {
            return this.createError({
              message: t('wanSettings.serviceType.required'),
            });
          }

          const items = value
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v);

          if (items.length === 0) {
            return this.createError({
              message: t('wanSettings.serviceType.required'),
            });
          }

          return items.every((item) =>
            serviceTypeOptions.includes(String(item))
          );
        }
      ),

    enable: Yup.boolean(),

    mtu: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required(t('wanSettings.mtu.required'))
      .positive()
      .integer()
      .test('mtu-limit', t('wanSettings.mtu.limit'), function (value) {
        return (value || 0) <= 1500;
      }),

    // --- IPv4 Global Rules ---
    ipv4Enable: Yup.boolean(),

    ipv4: Yup.string().when('ipv4Enable', {
      is: true,
      then: (schema) =>
        schema
          .matches(singleIpRegex, t('wanSettings.ipv4.invalid'))
          .required(t('wanSettings.ipv4.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    mask: Yup.string().when('ipv4Enable', {
      is: true,
      then: (schema) =>
        schema
          .matches(singleIpRegex, t('wanSettings.mask.invalid'))
          .required(t('wanSettings.mask.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    ipv4Gateway: Yup.string().when('ipv4Enable', {
      is: true,
      then: (schema) =>
        schema
          .matches(singleIpRegex, t('wanSettings.gateway.invalid'))
          .required(t('wanSettings.gateway.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    ipv4Dns: Yup.string().nullable().optional(),

    // --- IPv6 Global Rules ---
    ipv6Enable: Yup.boolean(),

    ipv6: Yup.string().when('ipv6Enable', {
      is: true,
      then: (schema) =>
        schema
          .matches(singleIpv6Regex, t('wanSettings.ipv6.invalid'))
          .required(t('wanSettings.ipv6.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    ipv6PrefixLength: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('ipv6Enable', {
        is: true,
        then: (schema) =>
          schema
            .required(t('wanSettings.ipv6Prefix.required'))
            .min(32, t('wanSettings.ipv6Prefix.range'))
            .max(128, t('wanSettings.ipv6Prefix.range')),
        otherwise: (schema) => schema.notRequired(),
      }),

    ipv6Gateway: Yup.string().when('ipv6Enable', {
      is: true,
      then: (schema) =>
        schema
          .matches(singleIpv6Regex, t('wanSettings.ipv6Gateway.invalid'))
          .required(t('wanSettings.ipv6Gateway.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    ipv6AddressingType: Yup.string()
      .nullable()
      .when('ipv6Enable', {
        is: true,
        then: (schema) =>
          schema
            .required(t('wanSettings.ipv6AddressingType.required'))
            .oneOf(
              ipv6AddressingTypeOptions,
              t('wanSettings.ipv6AddressingType.invalid')
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

    ipv6Dns: Yup.string().nullable().optional(),

    // --- VLAN Rules ---
    vlanEnable: Yup.boolean(),

    vlan: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('vlanEnable', {
        is: true,
        then: (schema) =>
          schema
            .required(t('wanSettings.vlan.required'))
            .min(1, t('wanSettings.vlan.range'))
            .max(4094, t('wanSettings.vlan.range')),
        otherwise: (schema) => schema.notRequired(),
      }),

    // --- NAT Rules ---
    nat: Yup.boolean(),
    fullConeNat: Yup.boolean(),
    natBoost: Yup.boolean(),

    // --- Read Only / Optional fields ---
    alias: Yup.string().nullable(),
    macAddress: Yup.string().nullable(),
  });

  return schema as unknown as Yup.ObjectSchema<Record<string, unknown>>;
};

// aux MACAddress validation regex (not used in current implementation, but can be added if needed)
//const macAddressRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;

export const getWifiSchema = (
  t: (key: string) => string,
  externalOptions?: ExternalOptions
) => {
  const authOptions = externalOptions?.authentication?.options || [
    'None',
    'WPA-Personal',
    'WPA2-Personal',
    'WPA3-Personal',
    'WPA-WPA2-Personal',
    'WPA2-WPA3-Personal',
    'WPA-Enterprise',
    'WPA2-Enterprise',
  ];

  const encryptionOptions = externalOptions?.encryption?.options || [
    'AES',
    'TKIP',
  ];

  const schema = Yup.object().shape({
    ssid: Yup.string()
      .required(t('wifiSettings.ssid.required'))
      .min(3, t('wifiSettings.ssid.min'))
      .max(30, t('wifiSettings.ssid.max'))
      .matches(
        /^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?\s]*$/,
        t('wifiSettings.ssid.invalidChars')
      ),
    authentication: Yup.string()
      .required(t('wifiSettings.auth.required'))
      .oneOf(authOptions, t('wifiSettings.auth.invalid')),
    password: Yup.string().when('authentication', {
      is: (authValue: string) => authValue !== 'ABERTA',
      then: (schema) =>
        schema
          .required(t('wifiSettings.password.required'))
          .min(8, t('wifiSettings.password.min'))
          .max(32, t('wifiSettings.password.max'))
          .matches(/[a-z]/, t('wifiSettings.password.lowercaseRequired'))
          .matches(/[A-Z]/, t('wifiSettings.password.uppercaseRequired')),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    encryption: Yup.string().when('authentication', {
      is: (authValue: string) => ['WPA', 'WPA2', 'WPA3'].includes(authValue),
      then: (schema) =>
        schema
          .required(t('wifiSettings.encryption.required'))
          .oneOf(encryptionOptions, t('wifiSettings.encryption.invalid')),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    ssidEnable: Yup.boolean().required(),
    apEnable: Yup.boolean().required(),
    visible: Yup.boolean().required(),
    maxHosts: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .typeError(t('wifiSettings.maxHosts.number'))
      .integer(t('wifiSettings.maxHosts.integer'))
      .min(0, t('wifiSettings.maxHosts.min')) // doesn't allow negative numbers
      .max(64, t('wifiSettings.maxHosts.max'))
      .required(t('wifiSettings.maxHosts.required')),
    macAddress: Yup.string(),
    frequency: Yup.string().optional(),
  });

  return schema as unknown as Yup.ObjectSchema<Record<string, unknown>>;
};

export const getRadiosSchema = (
  t: (key: string) => string,
  externalOptions?: ExternalOptions
) => {
  // Helper to parse comma-separated strings (e.g., "1, 6, 11") into an array for validation
  const parseSupported = (val: unknown): string[] => {
    if (typeof val !== 'string' || !val) return [];
    return val
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  };

  const statusOptions = externalOptions?.status?.options || [
    'UP',
    'DOWN',
    'UNKNOWN',
  ];

  const schema = Yup.object().shape({
    // --- Context & Support Fields (Used for logic) ---
    frequency: Yup.string().nullable(),
    bandWidthSupported: Yup.string().nullable().optional(),
    channelsPossible: Yup.string().nullable().optional(),
    transmitPowerSupported: Yup.string().nullable().optional(),

    // --- Configuration Fields ---

    // 1. Enable
    enable: Yup.boolean().required(t('radios.enable.required')),

    // 2. Status
    status: Yup.string()
      .nullable()
      .oneOf([...statusOptions, null, ''], t('radios.status.invalid')),

    // 3. Bandwidth
    bandWidthOperation: Yup.string()
      .required(t('radios.bandwidth.required'))
      .when(['bandWidthSupported'], ([supported], schema) => {
        const options = parseSupported(supported);

        // Dynamic validation: strict check against supported list if available
        if (options.length > 0) {
          return schema.oneOf(options, t('radios.bandwidth.invalid'));
        }

        // Fallback: accept any string if the backend didn't provide a list
        return schema;
      }),

    bandWidthCurrent: Yup.string().nullable().optional(),

    // 4. Channel Auto
    channelAuto: Yup.boolean().required(t('radios.channelAuto.required')),

    // 5. Channel
    channel: Yup.string().when('channelAuto', {
      is: true,
      then: (schema) => schema.nullable().notRequired(),
      otherwise: (schema) =>
        schema
          .required(t('radios.channel.required'))
          .when(['channelsPossible'], ([possible], schema) => {
            const options = parseSupported(possible);

            // Dynamic validation: strict check against supported list
            if (options.length > 0) {
              return schema.oneOf(options, t('radios.channel.invalid'));
            }

            // Fallback: generic numeric check to avoid blocking valid channels (e.g., regulatory differences)
            return schema.matches(/^\d+$/, t('radios.channel.invalidFormat'));
          }),
    }),

    // 6. Channels in Use (Read-only)
    channelsInUse: Yup.string().nullable().optional(),

    // 7. Transmit Power
    transmitPower: Yup.string()
      .required(t('radios.transmitPower.required'))
      .when(['transmitPowerSupported'], ([supported], schema) => {
        const options = parseSupported(supported);

        // Dynamic validation based on supported power levels
        if (options.length > 0) {
          return schema.oneOf(options, t('radios.transmitPower.invalid'));
        }

        return schema;
      }),

    // --- Traffic Statistics (Read-only) ---
    bytesSent: Yup.number().integer().nullable().optional(),
    bytesReceived: Yup.number().integer().nullable().optional(),
    packetsSent: Yup.number().integer().nullable().optional(),
    packetsReceived: Yup.number().integer().nullable().optional(),
    errorsSent: Yup.number().integer().nullable().optional(),
    errorsReceived: Yup.number().integer().nullable().optional(),
    discardPacketsSent: Yup.number().integer().nullable().optional(),
    discardPacketsReceived: Yup.number().integer().nullable().optional(),
  });

  return schema as unknown as Yup.ObjectSchema<Record<string, unknown>>;
};

// Cross and general validations about VoIP (SIP) Settings
export const getVoipSchema = (t: (key: string) => string) => {
  const portSchema = (requiredKey: string, invalidKey: string) =>
    Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('enable', {
        is: true,
        then: (schema) =>
          schema
            .required(t(requiredKey))
            .integer(t(invalidKey))
            .min(1, t(invalidKey))
            .max(65535, t(invalidKey)),
        otherwise: (schema) => schema.notRequired().nullable(),
      });

  const schema = Yup.object().shape({
    enable: Yup.boolean(),

    // --- Auth Rules ---
    user: Yup.string().when('enable', {
      is: true,
      then: (schema) => schema.required(t('voipSettings.user.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    password: Yup.string().when('enable', {
      is: true,
      then: (schema) => schema.required(t('voipSettings.password.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    // --- Proxy Server Rules ---
    serverProxy: Yup.string().when('enable', {
      is: true,
      then: (schema) =>
        schema.required(t('voipSettings.serverProxy.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    serverProxyPort: portSchema(
      'voipSettings.serverProxyPort.required',
      'voipSettings.serverProxyPort.invalid'
    ),

    // --- Register Server Rules ---
    registryServer: Yup.string().when('enable', {
      is: true,
      then: (schema) =>
        schema.required(t('voipSettings.registryServer.required')),
      otherwise: (schema) => schema.notRequired(),
    }),

    registryServerPort: portSchema(
      'voipSettings.registryServerPort.required',
      'voipSettings.registryServerPort.invalid'
    ),

    // --- Outbound Proxy Rules (optional) ---
    proxy: Yup.string().nullable().notRequired(),

    proxyPort: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('proxy', {
        is: (value: string) => Boolean(value && value.trim()),
        then: (schema) =>
          schema
            .required(t('voipSettings.proxyPort.required'))
            .integer(t('voipSettings.proxyPort.invalid'))
            .min(1, t('voipSettings.proxyPort.invalid'))
            .max(65535, t('voipSettings.proxyPort.invalid')),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),

    // --- Read Only fields (to allow pass-through) ---
    status: Yup.string().nullable(),
  });

  return schema as unknown as Yup.ObjectSchema<Record<string, unknown>>;
};
